import React, { useState, useEffect } from 'react';
import { PILLAR_DEFS } from '../../config/pillarDefs';
import { ShieldCheck, ChevronDown, Eye, Bot } from 'lucide-react';

const STANDARD_ROLES = [
    { value: '', label: 'No Access' },
    { value: 'user', label: 'User' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Admin' },
];

const CUSTOM_MODULE_ROLES = [
    { value: '', label: 'No Access' },
    { value: 'user', label: 'User' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Admin' },
    { value: 'developer', label: 'Developer' },
];

const ROLE_COLORS = {
    admin: 'bg-stone-900 text-white',
    manager: 'bg-stone-200 text-stone-800',
    user: 'bg-stone-100 text-stone-600',
    developer: 'bg-indigo-100 text-indigo-800',
    '': 'bg-stone-50 text-stone-400',
};

/**
 * PillarRoleEditor — Grid for assigning per-pillar roles to a user.
 *
 * Props:
 *   orgRole: "org_admin" | "member"
 *   pillarRoles: { [pillarId]: { role: string, read_only: boolean } }
 *   onChange: (orgRole, pillarRoles, isAgentDeveloper) => void
 *   customModules: [{ module_id, name, icon }]  (optional)
 *   isAgentDeveloper: boolean
 */
export default function PillarRoleEditor({ orgRole, pillarRoles, onChange, customModules = [], isAgentDeveloper = false }) {
    const [localOrgRole, setLocalOrgRole] = useState(orgRole || 'member');
    const [localPillarRoles, setLocalPillarRoles] = useState(pillarRoles || {});
    const [localAgentDev, setLocalAgentDev] = useState(isAgentDeveloper || false);

    useEffect(() => {
        setLocalOrgRole(orgRole || 'member');
        setLocalPillarRoles(pillarRoles || {});
        setLocalAgentDev(isAgentDeveloper || false);
    }, [orgRole, pillarRoles, isAgentDeveloper]);

    const handleOrgRoleChange = (newOrgRole) => {
        setLocalOrgRole(newOrgRole);
        onChange(newOrgRole, localPillarRoles, localAgentDev);
    };

    const handleAgentDevToggle = () => {
        const newVal = !localAgentDev;
        setLocalAgentDev(newVal);
        onChange(localOrgRole, localPillarRoles, newVal);
    };

    const handlePillarRoleChange = (pillarId, role) => {
        const updated = { ...localPillarRoles };
        if (!role) {
            delete updated[pillarId];
        } else {
            updated[pillarId] = {
                role,
                read_only: updated[pillarId]?.read_only || false,
            };
        }
        setLocalPillarRoles(updated);
        onChange(localOrgRole, updated, localAgentDev);
    };

    const handleReadOnlyToggle = (pillarId) => {
        const current = localPillarRoles[pillarId];
        if (!current) return;
        const updated = {
            ...localPillarRoles,
            [pillarId]: { ...current, read_only: !current.read_only },
        };
        setLocalPillarRoles(updated);
        onChange(localOrgRole, updated, localAgentDev);
    };

    const handleApplyToAll = (role) => {
        const updated = {};
        const allPillars = [...PILLAR_DEFS.filter(p => !p.comingSoon), ...customModules.map(m => ({ id: m.module_id }))];
        for (const p of allPillars) {
            if (role) {
                updated[p.id] = { role, read_only: false };
            }
        }
        setLocalPillarRoles(updated);
        onChange(localOrgRole, updated, localAgentDev);
    };

    // Build the list of pillars to show
    const standardPillars = PILLAR_DEFS.filter(p => !p.comingSoon);
    const comingSoonPillars = PILLAR_DEFS.filter(p => p.comingSoon);

    return (
        <div className="space-y-4">
            {/* Organization Role */}
            <div>
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                    Organization Role
                </label>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => handleOrgRoleChange('member')}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                            localOrgRole === 'member'
                                ? 'border-stone-900 bg-stone-900 text-white'
                                : 'border-stone-200 text-stone-600 hover:bg-stone-50'
                        }`}
                    >
                        Member
                    </button>
                    <button
                        type="button"
                        onClick={() => handleOrgRoleChange('org_admin')}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                            localOrgRole === 'org_admin'
                                ? 'border-stone-900 bg-stone-900 text-white'
                                : 'border-stone-200 text-stone-600 hover:bg-stone-50'
                        }`}
                    >
                        <div className="flex items-center justify-center gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            Org Admin
                        </div>
                    </button>
                </div>
                {localOrgRole === 'org_admin' && (
                    <p className="mt-2 text-xs text-stone-500">
                        Org Admins have full access to all departments and settings.
                    </p>
                )}
            </div>

            {/* AI Agent Developer Toggle */}
            <div>
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                    Special Permissions
                </label>
                <button
                    type="button"
                    onClick={handleAgentDevToggle}
                    className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                        localAgentDev
                            ? 'border-violet-200 bg-violet-50/50'
                            : 'border-stone-200 bg-white hover:bg-stone-50'
                    }`}
                >
                    <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            localAgentDev ? 'bg-violet-100' : 'bg-stone-100'
                        }`}>
                            <Bot className={`w-4 h-4 ${localAgentDev ? 'text-violet-600' : 'text-stone-400'}`} />
                        </div>
                        <div className="text-left">
                            <span className="text-sm font-medium text-stone-800">AI Agent Developer</span>
                            <p className="text-[11px] text-stone-500">
                                Can build and configure AI agents for CRM/ERP workflows
                            </p>
                        </div>
                    </div>
                    <div className={`w-9 h-5 rounded-full transition-colors relative ${
                        localAgentDev ? 'bg-violet-500' : 'bg-stone-200'
                    }`}>
                        <div className={`w-3.5 h-3.5 bg-white rounded-full absolute top-0.5 transition-transform ${
                            localAgentDev ? 'translate-x-4' : 'translate-x-0.5'
                        }`} />
                    </div>
                </button>
            </div>

            {/* Pillar Role Matrix */}
            {localOrgRole === 'member' && (
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                            Department Access
                        </label>
                        <div className="flex items-center gap-1">
                            <span className="text-[10px] text-stone-400 mr-1">Apply to all:</span>
                            {['user', 'manager', 'admin'].map(r => (
                                <button
                                    key={r}
                                    type="button"
                                    onClick={() => handleApplyToAll(r)}
                                    className="px-2 py-0.5 text-[10px] font-medium rounded bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors capitalize"
                                >
                                    {r}
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={() => handleApplyToAll('')}
                                className="px-2 py-0.5 text-[10px] font-medium rounded bg-stone-100 text-stone-400 hover:bg-stone-200 transition-colors"
                            >
                                None
                            </button>
                        </div>
                    </div>

                    <div className="border border-stone-200 rounded-lg overflow-hidden">
                        {/* Header */}
                        <div className="grid grid-cols-[1fr,140px,70px] bg-stone-50 px-3 py-2 border-b border-stone-200">
                            <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider">Department</span>
                            <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider">Role</span>
                            <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider text-center">Read Only</span>
                        </div>

                        {/* Standard pillars */}
                        {standardPillars.map((pillar) => {
                            const Icon = pillar.icon;
                            const pr = localPillarRoles[pillar.id];
                            const currentRole = pr?.role || '';
                            const readOnly = pr?.read_only || false;

                            return (
                                <div key={pillar.id} className="grid grid-cols-[1fr,140px,70px] items-center px-3 py-2 border-b border-stone-100 last:border-b-0">
                                    <div className="flex items-center gap-2">
                                        <Icon className="w-4 h-4 text-stone-400" />
                                        <span className="text-sm font-medium text-stone-700">{pillar.label}</span>
                                    </div>
                                    <select
                                        value={currentRole}
                                        onChange={(e) => handlePillarRoleChange(pillar.id, e.target.value)}
                                        className="text-sm border border-stone-200 rounded-md px-2 py-1 bg-white text-stone-700 focus:ring-1 focus:ring-stone-400 focus:outline-none"
                                    >
                                        {STANDARD_ROLES.map(r => (
                                            <option key={r.value} value={r.value}>{r.label}</option>
                                        ))}
                                    </select>
                                    <div className="flex justify-center">
                                        {currentRole ? (
                                            <button
                                                type="button"
                                                onClick={() => handleReadOnlyToggle(pillar.id)}
                                                className={`w-8 h-5 rounded-full transition-colors relative ${
                                                    readOnly ? 'bg-amber-500' : 'bg-stone-200'
                                                }`}
                                                title={readOnly ? 'Read Only' : 'Full Access'}
                                            >
                                                <div className={`w-3.5 h-3.5 bg-white rounded-full absolute top-0.5 transition-transform ${
                                                    readOnly ? 'translate-x-3.5' : 'translate-x-0.5'
                                                }`} />
                                            </button>
                                        ) : (
                                            <span className="text-stone-300">—</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Coming soon pillars (greyed out) */}
                        {comingSoonPillars.map((pillar) => {
                            const Icon = pillar.icon;
                            return (
                                <div key={pillar.id} className="grid grid-cols-[1fr,140px,70px] items-center px-3 py-2 border-b border-stone-100 last:border-b-0 opacity-40">
                                    <div className="flex items-center gap-2">
                                        <Icon className="w-4 h-4 text-stone-300" />
                                        <span className="text-sm font-medium text-stone-400">{pillar.label}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 bg-stone-100 text-stone-400 rounded-full">Soon</span>
                                    </div>
                                    <span className="text-xs text-stone-300">—</span>
                                    <span className="text-center text-stone-300">—</span>
                                </div>
                            );
                        })}

                        {/* Custom modules */}
                        {customModules.map((mod) => {
                            const pr = localPillarRoles[mod.module_id];
                            const currentRole = pr?.role || '';
                            const readOnly = pr?.read_only || false;

                            return (
                                <div key={mod.module_id} className="grid grid-cols-[1fr,140px,70px] items-center px-3 py-2 border-b border-stone-100 last:border-b-0 bg-indigo-50/30">
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded bg-indigo-100 flex items-center justify-center">
                                            <span className="text-[8px] font-bold text-indigo-600">C</span>
                                        </div>
                                        <span className="text-sm font-medium text-stone-700">{mod.name}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-600 rounded-full">Custom</span>
                                    </div>
                                    <select
                                        value={currentRole}
                                        onChange={(e) => handlePillarRoleChange(mod.module_id, e.target.value)}
                                        className="text-sm border border-stone-200 rounded-md px-2 py-1 bg-white text-stone-700 focus:ring-1 focus:ring-stone-400 focus:outline-none"
                                    >
                                        {CUSTOM_MODULE_ROLES.map(r => (
                                            <option key={r.value} value={r.value}>{r.label}</option>
                                        ))}
                                    </select>
                                    <div className="flex justify-center">
                                        {currentRole ? (
                                            <button
                                                type="button"
                                                onClick={() => handleReadOnlyToggle(mod.module_id)}
                                                className={`w-8 h-5 rounded-full transition-colors relative ${
                                                    readOnly ? 'bg-amber-500' : 'bg-stone-200'
                                                }`}
                                            >
                                                <div className={`w-3.5 h-3.5 bg-white rounded-full absolute top-0.5 transition-transform ${
                                                    readOnly ? 'translate-x-3.5' : 'translate-x-0.5'
                                                }`} />
                                            </button>
                                        ) : (
                                            <span className="text-stone-300">—</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap gap-3 mt-3">
                        {[
                            { role: 'Admin', desc: 'Full control' },
                            { role: 'Manager', desc: 'Manage team, approve' },
                            { role: 'User', desc: 'Standard access' },
                            { role: 'Developer', desc: 'Build custom modules' },
                        ].map(item => (
                            <div key={item.role} className="flex items-center gap-1.5">
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                    ROLE_COLORS[item.role.toLowerCase()] || 'bg-stone-100 text-stone-500'
                                }`}>{item.role}</span>
                                <span className="text-[10px] text-stone-400">{item.desc}</span>
                            </div>
                        ))}
                        <div className="flex items-center gap-1.5">
                            <Eye className="w-3 h-3 text-amber-500" />
                            <span className="text-[10px] text-stone-400">Read Only = view only, no edits</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
